/* ==========================================================================
   NAC ATOMS - behaviour engine
   One small script, no dependencies. Every effect is opt-in through data attributes
   and fails open: if anything throws, content stays visible and usable.

   data-reveal[="left|right|scale|blur"]   fade/slide in when scrolled into view
   data-reveal-delay="120"                 stagger, in ms
   data-parallax="0.18"                    background layer drifts against the scroll
   data-zoom="14"                          scroll-linked zoom (percent at full travel)
   data-scramble                           decode-text effect on a plain-text heading
   data-countdown="2026-12-01T09:00:00-05:00"   live countdown with [data-unit=d|h|m|s]
   data-portal                             breach doors + scope runway (sticky stage)
   data-runway                             staged scroll runway (tactical portal)
   data-keypad                             clearance keypad with vault door
   data-product-form / data-cart-open ...  AJAX cart + drawer
   ========================================================================== */
(function () {
  'use strict';
  if (window.__nacLoaded) { return; }
  window.__nacLoaded = true;

  var root = document.documentElement;
  var cfg = window.NAC || {};
  var reduced = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var raf = window.requestAnimationFrame || function (f) { return setTimeout(f, 16); };

  function $(s, r) { return (r || document).querySelector(s); }
  function $$(s, r) { return Array.prototype.slice.call((r || document).querySelectorAll(s)); }
  function clamp(v, a, b) { return Math.min(b, Math.max(a, v)); }
  function num(el, attr, fallback) { var v = parseFloat(el.getAttribute(attr)); return isNaN(v) ? fallback : v; }
  function pad(n, len) { var s = String(Math.max(0, Math.floor(n))); while (s.length < len) { s = '0' + s; } return s; }
  function safe(fn) { try { fn(); } catch (e) { if (window.console) { console.warn('[nac]', e); } } }

  /* ------------------------------------------------------------ frame bus -- */
  var frameFns = [];
  var ticking = false;
  function schedule() {
    if (ticking) { return; }
    ticking = true;
    raf(function () {
      ticking = false;
      for (var i = 0; i < frameFns.length; i++) { safe(frameFns[i]); }
    });
  }
  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule);
  window.addEventListener('load', schedule);
  frameFns.push(function () {
    var h = $('.nac-header');
    if (h) { h.classList.toggle('is-scrolled', (window.pageYOffset || root.scrollTop) > 24); }
  });

  /* ---------------------------------------------------------------- reveal -- */
  var io = null;
  function showEl(el) { el.classList.add('is-in'); }
  function initReveal() {
    var els = $$('[data-reveal]:not(.is-in)');
    if (!els.length) { return; }
    if (reduced || !('IntersectionObserver' in window) || !root.classList.contains('nac-reveal-on')) {
      els.forEach(showEl);
      return;
    }
    if (!io) {
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { showEl(e.target); io.unobserve(e.target); }
        });
      }, { threshold: 0.1, rootMargin: '0px 0px -5% 0px' });
    }
    els.forEach(function (el) {
      var d = el.getAttribute('data-reveal-delay');
      if (d) { el.style.setProperty('--reveal-delay', d + 'ms'); }
      io.observe(el);
    });
  }

  /* -------------------------------------------------------------- parallax -- */
  var parallaxItems = [];
  function initParallax() {
    parallaxItems = (cfg.parallax === false || reduced) ? [] : $$('[data-parallax]');
  }
  frameFns.push(function () {
    var vh = window.innerHeight;
    for (var i = 0; i < parallaxItems.length; i++) {
      var layer = parallaxItems[i];
      var wrap = layer.parentNode;
      var rect = wrap.getBoundingClientRect();
      if (rect.bottom < -300 || rect.top > vh + 300) { continue; }
      var speed = num(layer, 'data-parallax', 0.15);
      var centre = rect.top + rect.height / 2 - vh / 2;
      var limit = rect.height * 0.12;
      var shift = clamp(-centre * speed, -limit, limit);
      layer.style.transform = 'translate3d(0,' + shift.toFixed(1) + 'px,0)';
    }
  });

  /* ------------------------------------------------------------------ zoom -- */
  var zoomItems = [];
  function initZoom() {
    zoomItems = (cfg.zoom === false || reduced) ? [] : $$('[data-zoom]');
  }
  frameFns.push(function () {
    var vh = window.innerHeight;
    for (var i = 0; i < zoomItems.length; i++) {
      var el = zoomItems[i];
      var r = el.parentNode.getBoundingClientRect();
      if (r.bottom < 0 || r.top > vh) { continue; }
      var p = clamp(1 - (r.top + r.height) / (vh + r.height), 0, 1);
      var amount = num(el, 'data-zoom', 12) / 100;
      el.style.transform = 'scale(' + (1 + amount * p).toFixed(4) + ')';
    }
  });

  /* --------------------------------------------------------------- scramble -- */
  var GLYPHS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789#%&/\\<>';
  function scramble(el) {
    if (el.__nacScr) { return; }
    el.__nacScr = true;
    var finalText = el.getAttribute('data-scramble') || el.textContent;
    if (!finalText) { return; }
    el.setAttribute('aria-label', finalText);
    var start = null;
    var duration = 1100;
    function step(ts) {
      if (start === null) { start = ts; }
      var p = clamp((ts - start) / duration, 0, 1);
      var out = '';
      for (var i = 0; i < finalText.length; i++) {
        var ch = finalText.charAt(i);
        if (ch === ' ' || i < finalText.length * p) { out += ch; }
        else { out += GLYPHS.charAt(Math.floor(Math.random() * GLYPHS.length)); }
      }
      el.textContent = out;
      if (p < 1) { raf(step); } else { el.textContent = finalText; }
    }
    raf(step);
    setTimeout(function () { el.textContent = finalText; }, duration + 1200);
  }
  function initScramble() {
    if (cfg.scramble === false || reduced) { return; }
    $$('[data-scramble]').forEach(function (el) {
      if ('IntersectionObserver' in window) {
        var o = new IntersectionObserver(function (es) {
          if (es[0].isIntersecting) { o.disconnect(); scramble(el); }
        }, { threshold: 0.3 });
        o.observe(el);
      } else { scramble(el); }
    });
  }

  /* -------------------------------------------------------------- countdown -- */
  function initCountdown() {
    $$('[data-countdown]').forEach(function (box) {
      if (box.__nacCd) { return; }
      var value = box.getAttribute('data-countdown');
      var target = value ? new Date(value).getTime() : NaN;
      if (isNaN(target)) { box.hidden = true; return; }
      box.__nacCd = true;
      var timer = null;
      function tick() {
        var left = target - Date.now();
        if (left <= 0) {
          if (timer) { clearInterval(timer); }
          box.classList.add('is-live');
          var live = box.getAttribute('data-live-text');
          if (live) { box.textContent = live; }
          return;
        }
        var parts = { d: left / 86400000, h: (left % 86400000) / 3600000, m: (left % 3600000) / 60000, s: (left % 60000) / 1000 };
        $$('[data-unit]', box).forEach(function (u) {
          var k = u.getAttribute('data-unit');
          u.textContent = pad(parts[k], k === 'd' ? 2 : 2);
        });
      }
      tick();
      timer = setInterval(tick, 1000);
    });
  }

  /* ------------------------------------------------- breach portal (doors) -- */
  var portals = [];
  function initPortals() { portals = $$('[data-portal]'); }
  frameFns.push(function () {
    var vh = window.innerHeight;
    portals.forEach(function (el) {
      var rect = el.getBoundingClientRect();
      var travel = el.offsetHeight - vh;
      if (travel < vh * 0.15) { travel = vh * 0.5; }
      var p = clamp(-rect.top / travel, 0, 1);
      var breachSpan = num(el, 'data-breach-span', 0.3);
      var door = reduced ? 1 : clamp(p / breachSpan, 0, 1);
      var reticle = reduced ? 1 : clamp((p - 0.04) / 0.32, 0, 1);
      el.style.setProperty('--door', door.toFixed(4));
      el.style.setProperty('--reticle', reticle.toFixed(4));
      el.style.setProperty('--p', p.toFixed(4));
      var breached = door >= 0.999;
      if (breached) { el.setAttribute('data-breached', '1'); } else { el.removeAttribute('data-breached'); }
      var set = function (sel, text) { $$(sel, el).forEach(function (n) { n.textContent = text; }); };
      set('[data-hud-alt]', pad(71 + p * 420, 4) + 'M');
      set('[data-hud-range]', pad(p * 1200, 4) + 'M');
      set('[data-hud-pwr]', Math.round(74 + p * 26) + '%');
      set('[data-hud-mode]', breached ? el.getAttribute('data-mode-open') || 'UNSEALED' : el.getAttribute('data-mode-closed') || 'ARMORED');
      set('[data-hud-breach]', p < 0.04 ? 'READY' : (breached ? 'BREACHED' : 'ENGAGING'));
    });
  });

  /* --------------------------------------------- staged runway (tactical) -- */
  var runways = [];
  function initRunways() { runways = $$('[data-runway]'); }
  frameFns.push(function () {
    var vh = window.innerHeight;
    runways.forEach(function (el) {
      if (window.innerWidth <= 768) { el.setAttribute('data-stage', 'static'); return; }
      var total = el.offsetHeight - vh;
      if (total <= 0) { el.setAttribute('data-stage', 'static'); return; }
      var p = clamp(-el.getBoundingClientRect().top / total, 0, 1);
      var start = num(el, 'data-count-start', 5);
      var stage = 'gate';
      if (p >= 0.85) { stage = 'urban'; }
      else if (p >= 0.74) { stage = 'field'; }
      else if (p >= 0.45) { stage = 'open'; }
      else if (p >= 0.2) { stage = 'count'; }
      el.setAttribute('data-stage', stage);
      el.style.setProperty('--p', p.toFixed(4));
      var open = clamp((p - 0.45) / 0.29, 0, 1);
      el.style.setProperty('--spin', (open * 540).toFixed(1) + 'deg');
      el.style.setProperty('--open', open.toFixed(3));
      var count = Math.max(0, Math.round(start - clamp((p - 0.2) / 0.25, 0, 1) * start));
      var digit = $('[data-runway-digit]', el);
      if (digit) {
        var txt = pad(p < 0.2 ? start : count, 2);
        if (digit.textContent !== txt) {
          digit.textContent = txt;
          digit.classList.remove('is-rolling'); void digit.offsetWidth; digit.classList.add('is-rolling');
        }
      }
      var rail = $$('[data-rail] [data-rail-item]', el);
      var active = { gate: 0, count: count === 0 ? 2 : 1, open: 3, field: 4, urban: 5 }[stage];
      rail.forEach(function (r, i) { r.classList.toggle('is-active', i === active); });
      var fill = $('[data-runway-fill]', el);
      if (fill) { fill.style.width = (p * 100).toFixed(1) + '%'; }
    });
  });

  /* ----------------------------------------------------------------- keypad -- */
  function initKeypads() {
    $$('[data-keypad]').forEach(function (kp) {
      if (kp.__nacKp) { return; }
      kp.__nacKp = true;
      var code = String(kp.getAttribute('data-code') || '1984').replace(/\D/g, '');
      var len = code.length || 4;
      var redirect = kp.getAttribute('data-redirect');
      var key = 'nac-keypad-' + (kp.id || 'x');
      var entry = '';
      var locked = false;
      var dots = $$('[data-dot]', kp);
      var status = $('[data-status]', kp);

      function setStatus(t) { if (status) { status.textContent = t; } }
      function paint() {
        dots.forEach(function (d, i) { d.classList.toggle('is-on', i < entry.length); });
      }
      function unlock() {
        locked = true;
        kp.classList.add('is-unlocking');
        setStatus(kp.getAttribute('data-ok-text') || 'ACCESS GRANTED');
        setTimeout(function () { kp.classList.add('is-open'); }, 1300);
        try { if (kp.getAttribute('data-remember') === 'true') { sessionStorage.setItem(key, '1'); } } catch (e) { /* ignore */ }
        if (redirect) { setTimeout(function () { window.location.href = redirect; }, 2800); }
      }
      function submit() {
        if (locked) { return; }
        if (entry === code) { unlock(); return; }
        kp.classList.add('is-error');
        setStatus(kp.getAttribute('data-deny-text') || 'ACCESS DENIED');
        setTimeout(function () { kp.classList.remove('is-error'); entry = ''; paint(); setStatus(kp.getAttribute('data-wait-text') || 'WAITING'); }, 900);
      }
      function press(k) {
        if (locked) { return; }
        if (k === 'clr') { entry = ''; paint(); setStatus(kp.getAttribute('data-wait-text') || 'WAITING'); return; }
        if (k === 'ent') { submit(); return; }
        if (entry.length < len) { entry += k; paint(); setStatus('INPUT ' + entry.length + '/' + len); }
      }
      $$('[data-key]', kp).forEach(function (b) {
        b.addEventListener('click', function () { press(b.getAttribute('data-key')); });
      });
      kp.addEventListener('keydown', function (e) {
        if (/^[0-9]$/.test(e.key)) { press(e.key); }
        else if (e.key === 'Backspace') { entry = entry.slice(0, -1); paint(); }
        else if (e.key === 'Enter') { submit(); }
      });
      try {
        if (kp.getAttribute('data-remember') === 'true' && sessionStorage.getItem(key)) {
          locked = true; kp.classList.add('is-unlocking', 'is-open');
        }
      } catch (e) { /* ignore */ }
    });
  }

  /* ------------------------------------------------------------------- cart -- */
  var currency = cfg.currency || 'USD';
  function money(cents) {
    try { return new Intl.NumberFormat(cfg.locale || undefined, { style: 'currency', currency: currency }).format(cents / 100); }
    catch (e) { return '$' + (cents / 100).toFixed(2); }
  }
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  var drawer = null;
  function openDrawer() {
    drawer = drawer || $('#nac-cart-drawer');
    if (!drawer) { return false; }
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('nac-lock');
    var c = $('[data-drawer-close]', drawer); if (c) { c.focus(); }
    return true;
  }
  function closeDrawer() {
    drawer = drawer || $('#nac-cart-drawer');
    if (!drawer) { return; }
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('nac-lock');
  }
  function renderCart(cart) {
    $$('[data-cart-count]').forEach(function (n) {
      n.textContent = cart.item_count;
      n.hidden = cart.item_count === 0;
    });
    drawer = drawer || $('#nac-cart-drawer');
    if (!drawer) { return; }
    var body = $('[data-drawer-body]', drawer);
    var foot = $('[data-drawer-foot]', drawer);
    if (!cart.items.length) {
      body.innerHTML = '<div class="nac-drawer__empty"><p class="nac-label">YOUR CART IS EMPTY</p><a class="nac-btn nac-btn--sm" href="' + esc(cfg.shopUrl || '/collections/all') + '">START SHOPPING <span class="nac-arrow">→</span></a></div>';
      foot.hidden = true;
      return;
    }
    body.innerHTML = cart.items.map(function (it, i) {
      var variant = it.variant_title && it.variant_title !== 'Default Title' ? '<div class="nac-label nac-dim">' + esc(it.variant_title) + '</div>' : '';
      var img = it.image ? '<img src="' + esc(it.image.replace(/(\.[a-z]+)(\?|$)/i, '_160x$1$2')) + '" alt="' + esc(it.featured_image && it.featured_image.alt || it.product_title) + '" width="80" height="100" loading="lazy">' : '';
      return '<div class="nac-line" data-line="' + (i + 1) + '"><a class="nac-line__img" href="' + esc(it.url) + '">' + img + '</a>' +
        '<div class="nac-line__info"><a class="nac-line__title" href="' + esc(it.url) + '">' + esc(it.product_title) + '</a>' + variant +
        '<div class="nac-line__row"><div class="nac-qty"><button type="button" data-qty="-1" aria-label="Decrease quantity">−</button><span>' + it.quantity + '</span><button type="button" data-qty="1" aria-label="Increase quantity">+</button></div>' +
        '<span class="nac-price">' + money(it.final_line_price) + '</span></div></div>' +
        '<button type="button" class="nac-line__remove" data-remove aria-label="Remove ' + esc(it.product_title) + '">×</button></div>';
    }).join('');
    foot.hidden = false;
    var total = $('[data-drawer-total]', foot);
    if (total) { total.textContent = money(cart.total_price); }
  }
  function getCart() {
    return fetch((cfg.routes && cfg.routes.cart || '/cart') + '.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
  }
  function changeLine(line, qty) {
    return fetch((cfg.routes && cfg.routes.cartChange || '/cart/change') + '.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: line, quantity: qty })
    }).then(function (r) { return r.json(); }).then(renderCart);
  }
  function initCart() {
    if (window.__nacCartBound) { return; }
    window.__nacCartBound = true;
    document.addEventListener('click', function (e) {
      var t = e.target;
      var openBtn = t.closest && t.closest('[data-cart-open]');
      if (openBtn && cfg.cartType === 'drawer' && $('#nac-cart-drawer')) {
        e.preventDefault();
        getCart().then(function (c) { renderCart(c); openDrawer(); }).catch(function () { window.location.href = openBtn.getAttribute('href') || '/cart'; });
        return;
      }
      if (t.closest && (t.closest('[data-drawer-close]') || t.closest('[data-drawer-backdrop]'))) { closeDrawer(); return; }
      var lineEl = t.closest && t.closest('[data-line]');
      if (lineEl) {
        var line = parseInt(lineEl.getAttribute('data-line'), 10);
        var q = t.closest('[data-qty]');
        if (q) {
          var current = parseInt($('.nac-qty span', lineEl).textContent, 10);
          changeLine(line, Math.max(0, current + parseInt(q.getAttribute('data-qty'), 10)));
        } else if (t.closest('[data-remove]')) { changeLine(line, 0); }
      }
    });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') { closeDrawer(); } });
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form.matches || !form.matches('[data-product-form]') || cfg.cartType !== 'drawer') { return; }
      e.preventDefault();
      var btn = $('[type="submit"]', form);
      var err = $('[data-form-error]', form);
      if (err) { err.textContent = ''; }
      var label = btn ? btn.querySelector('[data-btn-label]') : null;
      var original = label ? label.textContent : '';
      if (btn) { btn.setAttribute('aria-disabled', 'true'); }
      fetch((cfg.routes && cfg.routes.cartAdd || '/cart/add') + '.js', { method: 'POST', body: new FormData(form), headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, body: j }; }); })
        .then(function (res) {
          if (!res.ok) { if (err) { err.textContent = res.body.description || res.body.message || 'Could not add this item.'; } return null; }
          return getCart().then(function (c) {
            renderCart(c); openDrawer();
            if (label) { label.textContent = 'ADDED'; setTimeout(function () { label.textContent = original; }, 1600); }
          });
        })
        .catch(function () { form.submit(); })
        .then(function () { if (btn) { btn.removeAttribute('aria-disabled'); } });
    });
  }

  /* ---------------------------------------------- product: gallery + variants -- */
  function initProducts() {
    $$('[data-product]').forEach(function (p) {
      if (p.__nacPdp) { return; }
      p.__nacPdp = true;
      var jsonEl = $('[data-product-json]', p);
      var product = null;
      try { product = jsonEl ? JSON.parse(jsonEl.textContent) : null; } catch (e) { product = null; }

      // gallery thumbs
      function showMedia(id) {
        $$('[data-media]', p).forEach(function (m) { m.classList.toggle('is-active', m.getAttribute('data-media') === String(id)); });
        $$('[data-thumb]', p).forEach(function (t) { t.classList.toggle('is-active', t.getAttribute('data-thumb') === String(id)); t.setAttribute('aria-current', t.getAttribute('data-thumb') === String(id) ? 'true' : 'false'); });
      }
      $$('[data-thumb]', p).forEach(function (t) {
        t.addEventListener('click', function () { showMedia(t.getAttribute('data-thumb')); });
      });
      // hover zoom
      $$('[data-hover-zoom]', p).forEach(function (z) {
        z.addEventListener('mousemove', function (e) {
          var r = z.getBoundingClientRect();
          z.style.setProperty('--zx', ((e.clientX - r.left) / r.width * 100).toFixed(1) + '%');
          z.style.setProperty('--zy', ((e.clientY - r.top) / r.height * 100).toFixed(1) + '%');
        });
      });
      if (!product) { return; }

      var form = $('[data-product-form]', p);
      var idInput = form ? $('[name="id"]', form) : null;
      var priceEl = $('[data-price]', p);
      var btn = form ? $('[type="submit"]', form) : null;
      var btnLabel = btn ? $('[data-btn-label]', btn) : null;

      function selected() {
        var vals = [];
        $$('[data-option]', p).forEach(function (g) {
          var checked = $('input:checked', g);
          var sel = $('select', g);
          vals.push(checked ? checked.value : (sel ? sel.value : null));
        });
        return vals;
      }
      function findVariant(vals) {
        return product.variants.filter(function (v) {
          return vals.every(function (val, i) { return val === null || v.options[i] === val; });
        })[0];
      }
      function paintPrice(v) {
        if (!priceEl) { return; }
        var html = v.compare_at_price && v.compare_at_price > v.price
          ? '<span class="nac-sale">' + money(v.price) + '</span><s>' + money(v.compare_at_price) + '</s>'
          : money(v.price);
        priceEl.innerHTML = html;
      }
      function update() {
        var v = findVariant(selected());
        if (!v) {
          if (btn) { btn.setAttribute('aria-disabled', 'true'); }
          if (btnLabel) { btnLabel.textContent = 'UNAVAILABLE'; }
          return;
        }
        if (idInput) { idInput.value = v.id; }
        paintPrice(v);
        if (btn) {
          if (v.available) { btn.removeAttribute('aria-disabled'); } else { btn.setAttribute('aria-disabled', 'true'); }
          if (btnLabel) { btnLabel.textContent = v.available ? (btn.getAttribute('data-label-add') || 'ADD TO CART') : (btn.getAttribute('data-label-sold') || 'SOLD OUT'); }
        }
        if (v.featured_media && v.featured_media.id) { showMedia(v.featured_media.id); }
        var mainImg = $('[data-main-img]', p);
        if (mainImg && v.featured_media && v.featured_media.id) {
          var cand = $('[data-media-img="' + v.featured_media.id + '"]', p);
          if (cand && cand !== mainImg && cand.getAttribute('data-main-src')) {
            mainImg.src = cand.getAttribute('data-main-src');
            var ss = cand.getAttribute('data-main-srcset'); if (ss) { mainImg.srcset = ss; }
            var al = cand.getAttribute('data-main-alt'); if (al !== null) { mainImg.alt = al; }
          }
        }
        if (window.history && history.replaceState && p.getAttribute('data-update-url') === 'true') {
          var u = new URL(window.location.href); u.searchParams.set('variant', v.id); history.replaceState({}, '', u.toString());
        }
        $$('[data-option]', p).forEach(function (g, i) {
          var out = $('[data-chosen]', g); if (!out) { return; }
          var checked = $('input:checked', g);
          var lbl = checked ? checked.getAttribute('data-display-label') : null;
          out.textContent = lbl || v.options[i];
        });
      }
      $$('[data-option] input, [data-option] select', p).forEach(function (i) { i.addEventListener('change', update); });
    });
  }

  /* ------------------------------------------------------------ misc widgets -- */
  function initRecommendations() {
    $$('[data-recommendations]').forEach(function (box) {
      if (box.__nacReco) { return; }
      box.__nacReco = true;
      var url = box.getAttribute('data-url');
      if (!url || !window.fetch) { return; }
      fetch(url).then(function (r) { return r.text(); }).then(function (html) {
        var tmp = document.createElement('div');
        tmp.innerHTML = html;
        var items = tmp.querySelector('[data-reco-items]');
        if (items && items.children.length) { box.innerHTML = items.innerHTML; box.hidden = false; initReveal(); }
      }).catch(function () { /* optional block */ });
    });
  }
  function initForms() {
    $$('[data-auto-submit]').forEach(function (f) {
      if (f.__nacAuto) { return; }
      f.__nacAuto = true;
      f.addEventListener('change', function () { if (f.requestSubmit) { f.requestSubmit(); } else { f.submit(); } });
    });
  }
  function initMenu() {
    if (window.__nacMenuBound) { return; }
    window.__nacMenuBound = true;
    document.addEventListener('click', function (e) {
      var tgl = e.target.closest && e.target.closest('[data-menu-toggle]');
      if (tgl) {
        var open = document.body.classList.toggle('nac-menu-open');
        tgl.setAttribute('aria-expanded', open ? 'true' : 'false');
        return;
      }
      if (e.target.closest && e.target.closest('.nac-nav a')) { document.body.classList.remove('nac-menu-open'); }
    });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') { document.body.classList.remove('nac-menu-open'); } });
  }


  /* ------------------------------------------------- hero scroll cue (fade + drift) -- */
  var cues = [];
  function initCues() { cues = reduced ? [] : $$('[data-cue]'); }
  frameFns.push(function () {
    var y = window.pageYOffset || root.scrollTop;
    var p = clamp(y / (window.innerHeight * 0.45), 0, 1);
    for (var i = 0; i < cues.length; i++) {
      cues[i].style.opacity = (1 - p).toFixed(3);
      cues[i].style.transform = 'translate3d(0,' + (p * 36).toFixed(1) + 'px,0)';
      cues[i].style.pointerEvents = p > 0.9 ? 'none' : '';
    }
  });

  /* ------------------------------------------- mission: active rail drives the sticky panel -- */
  /* Smooth parallax: the active panel's media drifts continuously with scroll
     progress through the section, instead of sitting frozen between swaps. */
  function initMission() {
    $$('[data-mission]').forEach(function (sec) {
      if (sec.__nacMission) { return; }
      sec.__nacMission = true;
      var rails = $$('[data-rail]', sec);
      var panels = $$('[data-panel]', sec);
      if (!rails.length) { return; }
      function activate(i) {
        rails.forEach(function (r, k) { r.classList.toggle('is-active', k === i); });
        panels.forEach(function (p, k) { p.classList.toggle('is-active', k === i); });
      }
      if ('IntersectionObserver' in window) {
        var mo = new IntersectionObserver(function (entries) {
          entries.forEach(function (e) { if (e.isIntersecting) { activate(rails.indexOf(e.target)); } });
        }, { rootMargin: '-45% 0px -45% 0px', threshold: 0 });
        rails.forEach(function (r) { mo.observe(r); });
      }
      var driftTick = false;
      function drift() {
        driftTick = false;
        var rect = sec.getBoundingClientRect();
        var vh = window.innerHeight || 800;
        var prog = Math.min(1, Math.max(0, (vh - rect.top) / (vh + rect.height)));
        panels.forEach(function (p) {
          var media = p.querySelector('.nac-panel__media');
          if (!media) { return; }
          if (p.classList.contains('is-active')) {
            var y = ((prog - 0.5) * 48).toFixed(1);
            media.style.transform = 'translateY(' + y + 'px) scale(1.06)';
          } else {
            media.style.transform = '';
          }
        });
        rails.forEach(function (r) {
          var inline = r.querySelector('.nac-rail__inline');
          if (!inline) { return; }
          var rr = r.getBoundingClientRect();
          if (rr.bottom < -300 || rr.top > vh + 300) { return; }
          var centre = rr.top + rr.height / 2 - vh / 2;
          var lim = rr.height * 0.12;
          var sh = Math.max(-lim, Math.min(lim, -centre * 0.18));
          inline.style.transform = 'translate3d(0,' + sh.toFixed(1) + 'px,0)';
        });
      }
      function onScroll() { if (!driftTick) { driftTick = true; requestAnimationFrame(drift); } }
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
      drift();
    });
  }

  /* -------------------------------------------------------------------- boot -- */
  function init() {
    safe(initReveal);
    safe(initParallax);
    safe(initZoom);
    safe(initScramble);
    safe(initCountdown);
    safe(initPortals);
    safe(initRunways);
    safe(initKeypads);
    safe(initCart);
    safe(initProducts);
    safe(initRecommendations);
    safe(initForms);
    safe(initMenu);
    safe(initCues);
    safe(initMission);
    window.__nacBooted = true;
    schedule();
  }
  if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', init); } else { init(); }
  document.addEventListener('shopify:section:load', init);
  window.NACEngine = { init: init, openDrawer: openDrawer, closeDrawer: closeDrawer, renderCart: renderCart };
})();
